<template>
  <div class="app-textarea" :class="rootClasses">
    <label
      v-if="label"
      :for="textareaId"
      class="app-textarea__label"
    >
      {{ label }}
      <span v-if="showRequiredMark" class="app-textarea__required" aria-hidden="true">*</span>
    </label>

    <div class="app-textarea__field">
      <textarea
        :id="textareaId"
        ref="textarea"
        v-bind="$attrs"
        :value="value"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :maxlength="maxlength || null"
        :aria-invalid="!!error || null"
        :aria-describedby="describedBy"
        class="app-textarea__control"
        :class="{ 'app-textarea__control--no-resize': !resize }"
        @input="onInput"
        @blur="onBlur"
        @change="onChange"
        @focus="onFocus"
        @keydown="(e) => $emit('keydown', e)"
      ></textarea>
    </div>

    <div v-if="error || help || showCounter" class="app-textarea__meta">
      <small
        v-if="error"
        :id="errorId"
        class="app-textarea__error"
        v-html="error"
      ></small>
      <small
        v-else-if="help"
        :id="helpId"
        class="app-textarea__help"
        v-html="help"
      ></small>

      <small v-if="showCounter" class="app-textarea__counter">
        {{ currentLength }}<template v-if="maxlength"> / {{ maxlength }}</template>
      </small>
    </div>
  </div>
</template>

<script>
// Auto-incrementing ID for label↔textarea pairing when caller didn't pass `id`.
let uid = 0

/**
 * App-styled multi-line text field — no-Bootstrap, drop-in upgrade over `c-textarea`.
 *
 * Migration notes from c-textarea:
 *   - Same v-model contract (value + input event).
 *   - Same prop names: label, error, help, rows, requiredMark.
 *   - Extra knobs added: size, variant, disabled, readonly, placeholder,
 *     maxlength, showCounter, resize.
 *
 * Public methods (via $refs):
 *   - focus()
 *   - select()
 */
export default {
  name: 'AppTextarea',

  inheritAttrs: false,

  props: {
    value: {
      type: [String, Number, null],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    help: {
      type: String,
      default: '',
    },
    rows: {
      type: [Number, String],
      default: 4,
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'ghost'].includes(v),
    },
    requiredMark: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    // Native maxlength enforcement + drives the counter when `showCounter`.
    maxlength: {
      type: [Number, String],
      default: 0,
    },
    showCounter: {
      type: Boolean,
      default: false,
    },
    // Allow user-driven resize handle. Disabled by default — most consumers
    // prefer the field to keep its declared row height, and a free-floating
    // resize handle is a foot-gun inside modals and tight forms.
    resize: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
  },

  emits: ['input', 'blur', 'change', 'focus', 'keydown'],

  data() {
    return {
      autoId: `app-textarea-${++uid}`,
    }
  },

  computed: {
    textareaId() { return this.id || this.autoId },
    errorId()    { return `${this.textareaId}-error` },
    helpId()     { return `${this.textareaId}-help` },

    describedBy() {
      if (this.error) return this.errorId
      if (this.help)  return this.helpId
      return null
    },

    showRequiredMark() {
      return this.requiredMark || this.required
    },

    currentLength() {
      return String(this.value ?? '').length
    },

    rootClasses() {
      return [
        `app-textarea--${this.size}`,
        `app-textarea--${this.variant}`,
        {
          'app-textarea--has-error': !!this.error,
          'app-textarea--disabled':  this.disabled,
        },
      ]
    },
  },

  methods: {
    onInput(e)  { this.$emit('input', e.target.value) },
    onBlur(e)   { this.$emit('blur', e.target.value) },
    onChange(e) { this.$emit('change', e.target.value) },
    onFocus(e)  { this.$emit('focus', e) },

    /** Public — programmatically focus the inner textarea. */
    focus() {
      if (this.$refs.textarea) this.$refs.textarea.focus()
    },

    /** Public — programmatically select the textarea's contents. */
    select() {
      if (this.$refs.textarea) this.$refs.textarea.select()
    },
  },
}
</script>

<style lang="scss" scoped>
// Local design tokens — mirror app-input so the two controls visually rhyme.
$at-text:         #2b3a4a;
$at-text-muted:   #6c7a89;
$at-text-subtle:  #9aa6b3;
$at-bg-surface:   #ffffff;
$at-bg-soft:      #f6f8fa;
$at-border:       rgba(0, 0, 0, 0.10);
$at-blue-focus:   #4392d4;
$at-blue-ring:    rgba(67, 146, 212, 0.16);
$at-red:          #d04848;
$at-red-bg:       rgba(208, 72, 72, 0.08);
$at-red-ring:     rgba(208, 72, 72, 0.18);
$at-radius-md:    8px;
$at-radius-lg:    10px;
$at-duration:     140ms;
$at-ease:         cubic-bezier(.4, 0, .2, 1);

.app-textarea {
  display: flex;
  flex-direction: column;
  min-width: 0;

  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $at-text-muted;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 6px;
    line-height: 1.2;
  }

  &__required {
    color: $at-red;
    margin-left: 2px;
  }

  &__field {
    position: relative;
    display: flex;
    min-width: 0;
  }

  &__control {
    flex: 1;
    width: 100%;
    min-height: 88px;
    padding: 10px 12px;
    font-size: 13px;
    font-family: inherit;
    line-height: 1.5;
    color: $at-text;
    background: $at-bg-soft;
    border: 0.5px solid $at-border;
    border-radius: $at-radius-md;
    resize: vertical;
    transition:
      background  $at-duration $at-ease,
      border-color $at-duration $at-ease,
      box-shadow  $at-duration $at-ease,
      color       $at-duration $at-ease;

    &::placeholder { color: $at-text-subtle; }

    &:hover:not(:disabled):not(:read-only) {
      background: lighten($at-bg-soft, 1%);
    }

    &:focus {
      outline: none;
      background: $at-bg-surface;
      border-color: $at-blue-focus;
      box-shadow: 0 0 0 3px $at-blue-ring;
    }

    &:disabled,
    &[disabled] {
      color: $at-text-subtle;
      background: $at-bg-soft;
      cursor: not-allowed;
      opacity: 0.7;
    }

    &:read-only {
      cursor: default;
    }

    // Lock the height — the user can no longer drag-resize. Most forms
    // want a fixed shape per design; the resize prop opts back in.
    &--no-resize {
      resize: none;
    }
  }

  // ── Sizes ──
  &--sm &__control {
    min-height: 64px;
    padding: 8px 10px;
    font-size: 12px;
  }

  &--lg &__control {
    min-height: 120px;
    padding: 12px 14px;
    font-size: 14px;
    border-radius: $at-radius-lg;
  }

  // ── Variants ──
  &--ghost &__control {
    background: transparent;
    border-color: transparent;

    &:hover:not(:disabled):not(:read-only) {
      background: $at-bg-soft;
    }
    &:focus {
      background: $at-bg-surface;
      border-color: $at-blue-focus;
    }
  }

  // ── Error state ──
  &--has-error &__control {
    border-color: $at-red;
    background: $at-red-bg;

    &:focus { box-shadow: 0 0 0 3px $at-red-ring; }
  }

  // ── Disabled state (root-level kill switch) ──
  &--disabled {
    pointer-events: none;
    opacity: 0.7;
  }

  // ── Meta row (error / help + counter) ──
  &__meta {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-top: 4px;
  }

  &__error,
  &__help,
  &__counter {
    font-size: 11px;
    line-height: 1.4;
  }

  &__error   { color: $at-red; }
  &__help    { color: $at-text-muted; }
  &__counter { color: $at-text-subtle; margin-left: auto; }
}
</style>
